import React, { useState } from 'react';
import styled from 'styled-components';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import { Store, Users, Megaphone, DollarSign } from 'lucide-react';
import { backofficeApi, formatNumber, formatCurrency, formatGrowth } from '@kardume/services';
import { theme } from '@kardume/theme';
import type { MetricsRange } from '@kardume/types';
import { PageHeader, StatCard, Card, CardHeader, CardBody, Select } from '@kardume/ui';
import { useAsync } from '../utils/useAsync';
import DataState from '../components/DataState';
import VisitIntentReport from '../components/VisitIntentReport';

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const SectionSpacer = styled.div`
  height: 1rem;
`;

const Charts = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 1rem;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const RANGE_OPTIONS = [
  { value: '7d', label: 'Últimos 7 dias' },
  { value: '30d', label: 'Últimos 30 dias' },
  { value: '90d', label: 'Últimos 90 dias' },
  { value: '12m', label: 'Últimos 12 meses' },
];

const PIE_COLORS = [
  theme.primary,
  theme.accent,
  theme.info,
  theme.warning,
  theme.success,
  theme.error,
];

/** Backoffice platform reports: growth metrics, revenue and category breakdown. */
const ReportsPage: React.FC = () => {
  const [range, setRange] = useState<MetricsRange>('30d');
  const { data, loading, error, reload } = useAsync(
    () => backofficeApi.getDashboard(range),
    [range]
  );

  return (
    <>
      <PageHeader
        title="Relatórios"
        subtitle="Indicadores consolidados da plataforma Kardume Urbano."
        actions={
          <Select
            options={RANGE_OPTIONS}
            value={range}
            onChange={(e) => setRange(e.target.value as MetricsRange)}
          />
        }
      />

      <DataState loading={loading} error={error} onRetry={reload}>
        {data && (
          <>
            <Grid>
              <StatCard
                label="Lojistas"
                value={formatNumber(data.totalMerchants)}
                icon={<Store size={18} />}
                iconColor={theme.primary}
                delta={formatGrowth(data.merchantsGrowth)}
                deltaPositive={data.merchantsGrowth >= 0}
              />
              <StatCard
                label="Consumidores"
                value={formatNumber(data.totalCustomers)}
                icon={<Users size={18} />}
                iconColor={theme.info}
                delta={formatGrowth(data.customersGrowth)}
                deltaPositive={data.customersGrowth >= 0}
              />
              <StatCard
                label="Anúncios ativos"
                value={formatNumber(data.activePromotions)}
                icon={<Megaphone size={18} />}
                iconColor={theme.accent}
              />
              <StatCard
                label="Receita recorrente"
                value={formatCurrency(data.mrr)}
                icon={<DollarSign size={18} />}
                iconColor={theme.success}
                delta={formatGrowth(data.mrrGrowth)}
                deltaPositive={data.mrrGrowth >= 0}
              />
            </Grid>

            <Charts>
              <Card>
                <CardHeader>
                  <h3>Lojistas x Receita</h3>
                </CardHeader>
                <CardBody>
                  <ResponsiveContainer width="100%" height={280}>
                    <AreaChart data={mergeSeries(data.merchantsSeries, data.revenueSeries)}>
                      <defs>
                        <linearGradient id="rg1" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor={theme.primary} stopOpacity={0.35} />
                          <stop offset="95%" stopColor={theme.primary} stopOpacity={0} />
                        </linearGradient>
                        <linearGradient id="rg2" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor={theme.success} stopOpacity={0.3} />
                          <stop offset="95%" stopColor={theme.success} stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke={theme.border} />
                      <XAxis dataKey="date" tick={{ fontSize: 12, fill: theme.textMuted }} />
                      <YAxis tick={{ fontSize: 12, fill: theme.textMuted }} />
                      <Tooltip />
                      <Area
                        type="monotone"
                        dataKey="merchants"
                        name="Lojistas"
                        stroke={theme.primary}
                        fill="url(#rg1)"
                      />
                      <Area
                        type="monotone"
                        dataKey="revenue"
                        name="Receita"
                        stroke={theme.success}
                        fill="url(#rg2)"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </CardBody>
              </Card>

              <Card>
                <CardHeader>
                  <h3>Lojistas por categoria</h3>
                </CardHeader>
                <CardBody>
                  <ResponsiveContainer width="100%" height={280}>
                    <PieChart>
                      <Pie
                        data={data.merchantsByCategory}
                        dataKey="value"
                        nameKey="label"
                        cx="50%"
                        cy="50%"
                        outerRadius={100}
                      >
                        {data.merchantsByCategory.map((entry, index) => (
                          <Cell key={entry.label} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </CardBody>
              </Card>
            </Charts>
          </>
        )}
      </DataState>

      {/* Fora do `DataState` do painel: é outro endpoint e outra permissão
          (`ROLE_BACKOFFICE_REPORT_READ`). Quem não a tem precisa continuar
          vendo o resto da página em vez de bater num erro que engole tudo. */}
      <SectionSpacer />
      <VisitIntentReport />
    </>
  );
};

/** Merge merchants and revenue series into a single dataset keyed by date. */
function mergeSeries(
  merchants: { date: string; value: number }[],
  revenue: { date: string; value: number }[]
) {
  const map = new Map<string, { date: string; merchants: number; revenue: number }>();
  merchants.forEach((p) => map.set(p.date, { date: p.date, merchants: p.value, revenue: 0 }));
  revenue.forEach((p) => {
    const existing = map.get(p.date);
    if (existing) existing.revenue = p.value;
    else map.set(p.date, { date: p.date, merchants: 0, revenue: p.value });
  });
  return Array.from(map.values());
}

export default ReportsPage;
